import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import _ from 'lodash';

import { useHistory, useRouteMatch } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath } from 'react-router-dom';
import { FastField, Field, Form, Formik } from 'formik';

import { Button, Drawer, Grid, makeStyles } from '@material-ui/core';
import ListAltIcon from '@material-ui/icons/ListAlt';

import notification from 'app/config/notification';
import lineEndpoint from 'app/api/services/lineEndpoint';
import { errorHandler } from 'app/utils/errorHandler';
import { validateFormikForm } from 'app/utils/forms/functions';
import { apostilleInitialValues } from '../form-configs/apostille-initial-values';
import { apostillesValidationSchema } from '../form-configs/validation-schemas';

import { URLS } from 'app/config/urls';

import { fetchLine } from 'app/store/slices/line';

import Loading from 'app/layouts/Loading';
import PageWrapper from '../components/PageWrapper';
import SaveBox from '../components/SaveBox';
import CustomTextField from '../components/TextField';
import CustomDateField from '../components/DateField';
import RequestProcess from '../components/RequestProcess';
import FindBaseLegalAditamento from './drawers/FindBaseLegalAditamento';
import InfoLineCompany from '../components/InfoLineCompany';
import ApostilleModal from './modals/ApostilleModal';

import styles from './styles';
import { SERVICES_CODES } from '../../../config/services';

const useStyles = makeStyles(styles);

const ApostillesDetails = (props) => {
    const history = useHistory();
    const match = useRouteMatch();
    const dispatch = useDispatch();
    const classes = useStyles();

    const cdLinhaFromPath = React.useRef(match.params.cdLinha).current;
    const cdApostilleFromPath = React.useRef(match.params.cdLinhaApostila).current;
    const selectedLineStore = useSelector((state) => state.line.selected);
    const isStrip = useSelector((state) => state.line.isStrip);

    const servicesFilter = useRef([
        { cdServico: SERVICES_CODES.STRIP_PROLONGAMENTO_LINHA },
        { cdServico: SERVICES_CODES.STRIP_ENCURTAMENTO_LINHA },
        { cdServico: SERVICES_CODES.STRIP_REFORCO_HORARIOS },
        { cdServico: SERVICES_CODES.STRIP_CONEXAO_LINHAS },
        { cdServico: SERVICES_CODES.STRIP_ALTERACAO_DEFINITIVA_ITINERARIO },
        { cdServico: SERVICES_CODES.STRIP_ALTERACAO_PROVISORIA_ITINERARIO },
        { cdServico: SERVICES_CODES.STRIP_INCLUSAO_TIPO_EQUIPAMENTO },
        { cdServico: SERVICES_CODES.STRIP_SUBSTITUICAO_TIPO_EQUIPAMENTO },
        { cdServico: SERVICES_CODES.STRIP_IMPLANTACAO_SECAO },
        { cdServico: SERVICES_CODES.STRIP_SUPRESSAO_SECAO },
        { cdServico: SERVICES_CODES.STRIP_CANCELAMENTO_LINHA },
        { cdServico: SERVICES_CODES.STRIP_CANCELAMENTO_SERVICO },
        { cdServico: SERVICES_CODES.STRIP_MODIFICACAO_HORARIOS },
        { cdServico: SERVICES_CODES.STRIP_AMPLIACAO_HORARIOS },
        { cdServico: SERVICES_CODES.STRIP_SUPRESSAO_HORARIOS },
        { cdServico: SERVICES_CODES.STRIP_PRORROGACAO_PRAZO },
        { cdServico: SERVICES_CODES.STRIP_TRANSFERENCIA_LINHA },
    ]).current;

    const [loading, setLoading] = useState(true);
    const [loadingLine, setLoadingLine] = useState(true);
    const [requestProcessValues, setRequestProcessValues] = useState({});
    const [openDrawer, setOpenDrawer] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [modalData, setModalData] = useState({});
    const [deBaseLegal, setDeBaseLegal] = useState('');
    const [deAditamento, setDeAditamento] = useState('');
    const [listType, setListType] = useState('baseLegal');
    const [selectedApostille, setSelectedApostille] = useState(null);
    const [formInitials, setFormInitials] = useState(apostilleInitialValues);

    const canEdit = useMemo(() => {
        if (!selectedApostille) return true;
        return selectedApostille?.boAtivo;
    }, [selectedApostille]);

    useEffect(() => {
        (async () => {
            setLoadingLine(true);
            if (!selectedLineStore && cdLinhaFromPath) {
                await dispatch(fetchLine(cdLinhaFromPath));
            }
            setLoadingLine(false);
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLineStore]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                if (cdApostilleFromPath) {
                    if (!selectedApostille) {
                        const response = await lineEndpoint.getAposttile(cdApostilleFromPath);
                        if (response && response.status === 200) {
                            const apostille = response.data;
                            setSelectedApostille(apostille);
                            setDeBaseLegal(apostille.deBaseLegal);
                            setDeAditamento(apostille.deAditamento);
                            setFormInitials({ ...apostilleInitialValues, ...apostille });
                        }
                    }
                } else {
                    setFormInitials(apostilleInitialValues);
                }
                setLoading(false);
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cdApostilleFromPath, selectedLineStore]);

    const handleGenerateDocument = (data) => {
        setModalData({
            line: selectedLineStore,
            apostille: data,
        });
        setOpenModal(true);
    };

    const updateApostille = (data) => {
        setSelectedApostille(data);
        setFormInitials({ ...apostilleInitialValues, ...data });
    };

    const handleSetSelectedList = (data, formikBag) => {
        if (listType === 'baseLegal') {
            setDeBaseLegal(data.deDescricao);
            formikBag.setFieldValue('deBaseLegal', data.deDescricao);
            return;
        }
        setDeAditamento(data.deDescricao);
        formikBag.setFieldValue('deAditamento', data.deDescricao);
    };

    const handleSubmitForm = async (values, formikHelpers) => {
        try {
            setLoading(true);
            const valuesCopy = _.cloneDeep(values);

            const dtEmissao = moment(valuesCopy.dtEmissao);
            const dtVigencia = moment(valuesCopy.dtVigencia);
            if (dtEmissao.isAfter(dtVigencia)) {
                notification('A data de emissão deve ser anterior ou igual a data de vigência.', {
                    type: 'error',
                });
                return;
            }
            if (
                requestProcessValues.nuProcesso &&
                requestProcessValues.nuProcesso !== '' &&
                requestProcessValues.processError
            ) {
                notification('O número do processo não existe no CPA', {
                    type: 'error',
                });
                if (formikHelpers) {
                    formikHelpers.setFieldError('nuProcesso', 'O número do processo não existe no CPA');
                }
            }

            valuesCopy.cdLinha = selectedLineStore.cdLinha;
            valuesCopy.nuProcesso = requestProcessValues.nuProcesso;
            valuesCopy.nuSolicitacao = requestProcessValues.nuSolicitacaoFmt;
            valuesCopy.cdProcesso = requestProcessValues.cdProcesso;
            valuesCopy.cdSolicitacao = requestProcessValues.cdSolicitacao;
            valuesCopy.boAtivo = true;

            const saved = await lineEndpoint.saveApostille(valuesCopy);
            if (saved.status === 200) {
                notification('Apostila salva com sucesso', {
                    type: 'success',
                });
                updateApostille(saved.data);
                if (!cdApostilleFromPath) {
                    let params = {
                        cdLinha: cdLinhaFromPath,
                        cdLinhaApostila: saved.data.cdLinhaApostila,
                    };
                    if (isStrip) params.isStrip = 'strip';
                    history.push(generatePath(URLS.LINES_MANAGE_APOSTILLE_EDIT, params));
                }
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={formInitials}
                initialStatus={{ holding: false }}
                validationSchema={apostillesValidationSchema}
                validateOnChange={true}
                validateOnBlur={true}
                onSubmit={handleSubmitForm}>
                {(formProps) => {
                    const { handleSubmit, handleChange, values, setFieldTouched } = formProps;
                    return (
                        <Form>
                            <PageWrapper title="Cadastro / Edição de Apostila">
                                {loading && <Loading />}
                                <Grid container spacing={3}>
                                    <Grid item container spacing={3} direction={'row'}>
                                        <InfoLineCompany selectedLine={selectedLineStore} loadingLine={loadingLine} />
                                    </Grid>

                                    <Grid item xs={12} container spacing={2}>
                                        <Grid item xs={4}>
                                            <Field
                                                component={CustomTextField}
                                                label="Número do processo SEI"
                                                name="nuProcessoSei"
                                                inputProps={{
                                                    maxLength: 30,
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <RequestProcess
                                        {...props}
                                        {...formProps}
                                        initialState={formInitials}
                                        canEdit={canEdit}
                                        cdLinha={cdLinhaFromPath}
                                        servicesFilter={servicesFilter}
                                        requestProcessValues={requestProcessValues}
                                        setRequestProcessValues={setRequestProcessValues}
                                        fieldWidth={4}
                                    />

                                    <Grid item xs={12} sm={4}>
                                        <Field
                                            component={CustomTextField}
                                            label="Número de Apostila"
                                            name="nuApostilaFmt"
                                            fullWidth
                                            loading={loading}
                                            InputLabelProps={{ shrink: true }}
                                            disabled
                                            inputProps={{
                                                maxLength: 10,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Field
                                            component={CustomDateField}
                                            label="Válido a partir de"
                                            name="dtInicioValidade"
                                            disabled={!canEdit}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Field
                                            component={CustomDateField}
                                            label="Emissão"
                                            name="dtEmissao"
                                            disabled={!canEdit}
                                        />
                                    </Grid>

                                    <Grid item xs={8} sm={11}>
                                        <Field
                                            component={CustomTextField}
                                            label="Base legal"
                                            name="deBaseLegal"
                                            value={deBaseLegal}
                                            onChange={(e) => {
                                                handleChange(e);
                                                setFieldTouched('deBaseLegal');
                                                setDeBaseLegal(e.target.value);
                                            }}
                                            fullWidth
                                            disabled={loading || !canEdit}
                                            loading={loading}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{
                                                maxLength: 500,
                                            }}
                                        />
                                    </Grid>
                                    {canEdit && (
                                        <Grid item xs={3} sm={1} style={{ textAlign: 'center' }}>
                                            <Button
                                                disableElevation
                                                variant="contained"
                                                color="secondary"
                                                size="large"
                                                onClick={() => {
                                                    setListType('baseLegal');
                                                    setOpenDrawer(true);
                                                }}
                                                className={classes.btnListTextApostille}>
                                                <ListAltIcon />
                                            </Button>
                                        </Grid>
                                    )}

                                    <Grid item xs={8} sm={11}>
                                        <Field
                                            component={CustomTextField}
                                            label="Aditamento"
                                            name="deAditamento"
                                            value={deAditamento}
                                            onChange={(e) => {
                                                handleChange(e);
                                                setFieldTouched('deAditamento');
                                                setDeAditamento(e.target.value);
                                            }}
                                            fullWidth
                                            disabled={loading || !canEdit}
                                            loading={loading}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{
                                                maxLength: 500,
                                            }}
                                        />
                                    </Grid>
                                    {canEdit && (
                                        <Grid item xs={3} sm={1} style={{ textAlign: 'center' }}>
                                            <Button
                                                disableElevation
                                                variant="contained"
                                                color="secondary"
                                                size="large"
                                                onClick={() => {
                                                    setListType('adiantamento');
                                                    setOpenDrawer(true);
                                                }}
                                                className={classes.btnListTextApostille}>
                                                <ListAltIcon />
                                            </Button>
                                        </Grid>
                                    )}

                                    <Grid item xs={12}>
                                        <Field
                                            component={CustomTextField}
                                            label="Texto publicação"
                                            name="dePublicacao"
                                            multiline
                                            minRows={4}
                                            inputProps={{
                                                maxLength: 2000,
                                            }}
                                            disabled={!canEdit}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FastField
                                            component={CustomTextField}
                                            label="Observação"
                                            name="deObservacao"
                                            multiline
                                            minRows={4}
                                            inputProps={{
                                                maxLength: 2000,
                                            }}
                                            disabled={!canEdit}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <SaveBox
                                        onCancel={() => history.goBack()}
                                        onSave={() => {
                                            validateFormikForm(formProps, apostillesValidationSchema).then((res) => {
                                                if (Object.keys(res).length > 0) {
                                                    notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                                                        toastId: 'VALIDATION',
                                                        type: 'error',
                                                    });
                                                } else {
                                                    handleSubmit(values);
                                                }
                                            });
                                        }}
                                        firstButtonLabel={'VOLTAR'}
                                        middleButtonLabel={(() => {
                                            if (formProps.status.holding) {
                                                return 'Aguarde...';
                                            }
                                            if (canEdit) {
                                                return 'SALVAR';
                                            }
                                            return null;
                                        })()}
                                        middleButtonDisabled={formProps.status.holding}
                                        lastButtonDisabled={!canEdit || !values.cdLinhaApostila}
                                        lastButtonLabel={canEdit ? 'EMITIR DOCUMENTO' : null}
                                        onClickLastButton={() => handleGenerateDocument(values)}
                                    />
                                </Grid>
                            </PageWrapper>

                            <Drawer anchor="right" open={openDrawer} onClose={() => setOpenDrawer(false)}>
                                <FindBaseLegalAditamento
                                    onClose={() => setOpenDrawer(false)}
                                    onSave={(data) => handleSetSelectedList(data, formProps)}
                                    listType={listType}
                                />
                            </Drawer>
                        </Form>
                    );
                }}
            </Formik>

            <ApostilleModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                data={modalData}
                onUpdate={updateApostille}
            />
        </>
    );
};

export default ApostillesDetails;
